import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { ShieldCheck, TrendingUp, Building2, Loader2, ClipboardCheck, RotateCcw, UserRound, FileDown, Zap, Pencil } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import PotenzialiAgenzie from "@/components/PotenzialiAgenzie";
import ComplianceDetailDialog from "@/components/ComplianceDetailDialog";
import QuickAdd from "@/components/QuickAdd";
import VisitaB2BDialog from "@/components/VisitaB2BDialog";

function MonitoringStartEditor({ value, effective, onSaved }) {
  const [v, setV] = useState(value || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setV(value || ""), [value]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/direzionale/monitoring-start", { monitoring_start: v || null });
      toast.success("Data inizio monitoraggio salvata");
      onSaved?.();
    } catch { toast.error("Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="monitoring-start-editor">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Inizio monitoraggio</span>
      <input type="date" value={v} onChange={(e) => setV(e.target.value)}
        className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="monitoring-start-input" />
      <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid="monitoring-start-save">
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Salva"}
      </Button>
      {!v && <span className="text-[10px] text-muted-foreground">default: 1° gen ({effective ? new Date(effective).toLocaleDateString("it-IT") : "—"})</span>}
    </div>
  );
}

const PERIODS_UNUSED = [];
const STAGE_LABEL = { nuovo: "Non contattato", contattato: "Primo contatto", qualificato: "Parlato col decision maker", trattativa: "Incontro presso cliente/azienda", vinto: "Partner", ordine: "Ordine", perso: "Perso" };
const ESITI_FILTER = [["positivo", "Positivo"], ["interlocutorio", "Interlocutorio"], ["da_approfondire", "Da approfondire"], ["negativo", "Negativo"], ["nessun_interesse", "Nessun interesse"], ["non_effettuato", "Non effettuato"]];
const CATEGORIE = ["A", "B", "C", "D"];

const covBadge = (c) => {
  if (c == null) return "bg-slate-500/15 text-slate-300 border-slate-500/40";
  if (c >= 80) return "bg-green-500/15 text-green-400 border-green-500/40";
  if (c >= 50) return "bg-amber-500/15 text-amber-400 border-amber-500/40";
  return "bg-red-500/15 text-red-400 border-red-500/40";
};

const RespMetric = ({ label, value, pct, sotto, sub, testid, onClick, legend, inactive }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`w-full rounded-lg border p-4 text-left transition-colors hover:border-primary/60 hover:shadow-sm ${sotto ? "border-red-500/40 bg-red-500/10" : "border-border"}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      {sotto
        ? <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold text-red-400">sotto target</span>
        : <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-[10px] font-bold text-green-400">in linea</span>}
    </div>
    <div className="mt-1 font-heading text-3xl font-black">{value}</div>
    {pct != null && <Progress className="mt-2" value={Math.min(100, pct)} />}
    {sub && <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>}
    {inactive && inactive.length > 0 && (
      <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-semibold leading-tight text-amber-500" data-testid={`${testid}-inactive`}>
        {inactive.length} agenzi{inactive.length === 1 ? "a" : "e"} inattiv{inactive.length === 1 ? "a" : "e"} (valore 0) esclus{inactive.length === 1 ? "a" : "e"} dal calcolo: {inactive.join(", ")}
      </div>
    )}
    {legend && <div className="mt-2 border-t border-border pt-1.5 text-[10px] leading-tight text-muted-foreground">{legend}</div>}
    <div className="mt-1.5 text-[10px] font-semibold text-primary">Dettagli ›</div>
  </button>
);

const MONTHS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

function ConversioniChartTable({ list }) {
  const [selMonth, setSelMonth] = useState(null);
  const year = String(new Date().getFullYear());
  const rows = list || [];
  const counts = MONTHS_IT.map((label, i) => {
    const mm = String(i + 1).padStart(2, "0");
    return { m: mm, label, conversioni: rows.filter((r) => (r.date || "").slice(0, 7) === `${year}-${mm}`).length };
  });
  const shown = selMonth ? rows.filter((r) => (r.date || "").slice(0, 7) === `${year}-${selMonth}`) : rows;
  const selLabel = selMonth ? MONTHS_IT[parseInt(selMonth, 10) - 1] : null;
  const pickBar = (d) => { const mm = d?.m || d?.payload?.m; if (mm) setSelMonth((s) => (s === mm ? null : mm)); };
  return (
    <div className="space-y-3" data-testid="conversioni-chart-table">
      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={counts} margin={{ top: 10, right: 12, bottom: 4, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
          <Tooltip cursor={{ fill: "hsl(var(--muted))", opacity: 0.3 }}
            contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
            formatter={(v) => [v, "Partner convertiti"]} />
          <Bar dataKey="conversioni" radius={[4, 4, 0, 0]} maxBarSize={40} cursor="pointer" onClick={pickBar}>
            {counts.map((c) => <Cell key={c.m} fill={selMonth && selMonth !== c.m ? "#F59E0B66" : "#F59E0B"} data-testid={`conv-bar-${c.m}`} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground" data-testid="conv-chart-hint">
          {selMonth ? <><b>{selLabel} {year}</b>: {shown.length} conversioni</> : <>Tocca una barra per vedere l'elenco dei partner del mese · totale {rows.length}</>}
        </p>
        {selMonth && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setSelMonth(null)} data-testid="conv-reset-month">Tutti i mesi</Button>}
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead>Agenzia</TableHead><TableHead className="text-right">Valore</TableHead></TableRow></TableHeader>
        <TableBody>
          {shown.map((r, i) => (
            <TableRow key={i} data-testid={`resp-detail-conv-${i}`}><TableCell className="font-mono">{r.date}</TableCell><TableCell className="font-semibold">{r.name}</TableCell><TableCell>{r.agency_name}</TableCell><TableCell className="text-right font-mono">{eur(r.value)}</TableCell></TableRow>
          ))}
          {shown.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">{selMonth ? `Nessuna conversione a ${selLabel}.` : "Nessuna conversione nel periodo."}</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function AffNoteCell({ activityId, initial }) {
  const [val, setVal] = useState(initial || "");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/activities/${activityId}/notes`, { notes: draft });
      setVal(draft.trim());
      setEditing(false);
      toast.success("Nota aggiornata");
    } catch { toast.error("Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };
  if (editing) {
    return (
      <div className="space-y-1.5" data-testid={`aff-note-edit-${activityId}`}>
        <Textarea rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} className="text-xs" placeholder="Nota del responsabile sull'affiancamento…" data-testid={`aff-note-textarea-${activityId}`} />
        <div className="flex gap-1.5">
          <Button size="sm" className="h-7 px-2 text-xs" onClick={save} disabled={saving} data-testid={`aff-note-save-${activityId}`}>{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Salva"}</Button>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setDraft(val); setEditing(false); }}>Annulla</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="group flex cursor-pointer items-start gap-1.5" onClick={() => { setDraft(val); setEditing(true); }} data-testid={`aff-note-cell-${activityId}`}>
      <span className={`text-xs ${val ? "text-muted-foreground" : "italic text-primary/70"}`}>{val || "+ Aggiungi nota"}</span>
      <Pencil className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </div>
  );
}

function RespDetailDialog({ detail, onClose, resp, agencies }) {
  if (!detail) return null;
  const TITLES = {
    affiancamenti: "Affiancamenti svolti",
    conversioni: "Partner convertiti (rete)",
    compliance: "Compliance per agenzia",
    copertura_azioni: "Azioni su clienti per agenzia",
    copertura_fatturato: "Copertura vendite per agenzia",
    prospecting: "Prospect lavorati settimanali (rete)",
    pianificate_2w: "Attività pianificate (prossime 2 settimane)",
  };
  const agWithComp = agencies.filter((a) => a.compliance != null);
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="resp-detail-dialog">
        <DialogHeader>
          <DialogTitle>{TITLES[detail]}</DialogTitle>
          <DialogDescription>{detail === "conversioni" ? "Andamento su base annua (anno solare corrente), indipendente dalla data di monitoraggio." : "Dettaglio cumulativo sulla rete dall'inizio del monitoraggio a oggi."}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto">
          {detail === "conversioni" ? <ConversioniChartTable list={resp.conversioni_list_year || resp.conversioni_list} /> : (
          <Table>
            {detail === "affiancamenti" && (<>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Agenzia</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
              <TableBody>
                {resp.affiancamenti_list.map((r, i) => (
                  <TableRow key={i} data-testid={`resp-detail-aff-${i}`}><TableCell className="font-mono">{r.date}</TableCell><TableCell className="font-semibold">{r.agency_name}</TableCell><TableCell><AffNoteCell activityId={r.id} initial={r.notes} /></TableCell></TableRow>
                ))}
                {resp.affiancamenti_list.length === 0 && <TableRow><TableCell colSpan={3} className="py-8 text-center text-muted-foreground">Nessun affiancamento nel periodo.</TableCell></TableRow>}
              </TableBody>
            </>)}
            {detail === "prospecting" && (<>
              <TableHeader><TableRow><TableHead>Indicatore</TableHead><TableHead className="text-right">Valore</TableHead></TableRow></TableHeader>
              <TableBody>
                <TableRow><TableCell>Prospect distinti lavorati (periodo)</TableCell><TableCell className="text-right font-mono">{resp.prospecting_svolti}</TableCell></TableRow>
                <TableRow><TableCell>Prospect previsti nel periodo</TableCell><TableCell className="text-right font-mono">{resp.prospecting_previsti}</TableCell></TableRow>
                <TableRow><TableCell>Obiettivo settimanale della rete</TableCell><TableCell className="text-right font-mono">{resp.prospecting_settimana}/sett.</TableCell></TableRow>
                <TableRow><TableCell>Compliance</TableCell><TableCell className="text-right font-mono">{resp.prospecting_pct != null ? `${resp.prospecting_pct}%` : "—"}</TableCell></TableRow>
              </TableBody>
            </>)}
            {detail === "pianificate_2w" && (<>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Agenzia</TableHead><TableHead>Tipo</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
              <TableBody>
                {(resp.pianificate_2w_list || []).map((r, i) => (
                  <TableRow key={i} data-testid={`resp-detail-pian2w-${i}`}><TableCell className="font-mono">{r.date}</TableCell><TableCell className="font-semibold">{r.agency_name}</TableCell><TableCell className="text-xs">{r.type}</TableCell><TableCell className="text-xs text-muted-foreground">{r.notes}</TableCell></TableRow>
                ))}
                {(resp.pianificate_2w_list || []).length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Nessuna attività pianificata nelle prossime 2 settimane.</TableCell></TableRow>}
              </TableBody>
            </>)}
            {detail === "compliance" && (<>
              <TableHeader><TableRow><TableHead>Agenzia</TableHead><TableHead className="text-center">Compliance</TableHead><TableHead className="text-center">Effettuate</TableHead></TableRow></TableHeader>              <TableBody>
                {agWithComp.map((a) => (
                  <TableRow key={a.agency_id}><TableCell className="font-semibold">{a.agency_name}</TableCell><TableCell className="text-center font-mono">{a.compliance}%</TableCell><TableCell className="text-center font-mono">{a.compliance_effettuate}/{a.compliance_effettuate + a.compliance_non_effettuate}</TableCell></TableRow>
                ))}
                {agWithComp.length === 0 && <TableRow><TableCell colSpan={3} className="py-8 text-center text-muted-foreground">Nessun dato compliance.</TableCell></TableRow>}
              </TableBody>
            </>)}
            {detail === "copertura_azioni" && (<>
              <TableHeader><TableRow><TableHead>Agenzia</TableHead><TableHead className="text-center">Copertura</TableHead><TableHead className="text-center">Base presidio</TableHead></TableRow></TableHeader>
              <TableBody>
                {agencies.map((a) => (
                  <TableRow key={a.agency_id}><TableCell className="font-semibold">{a.agency_name}</TableCell><TableCell className="text-center font-mono">{a.copertura_adjusted != null ? `${a.copertura_adjusted}%` : "N/D"}</TableCell><TableCell className="text-center font-mono">{a.copertura_presidio != null ? `${a.copertura_presidio}%` : "N/D"}</TableCell></TableRow>
                ))}
              </TableBody>
            </>)}
            {detail === "copertura_fatturato" && (<>
              <div className="mb-2 text-[11px] text-muted-foreground" data-testid="copertura-fatturato-note">Obiettivo <b>progressivo</b>: obiettivo annuo di ogni agenzia rapportato ai giorni trascorsi dell'anno{resp?.prorata_pct != null ? ` (${resp.prorata_pct}% dell'anno)` : ""}.</div>
              <Table>
              <TableHeader><TableRow><TableHead>Agenzia</TableHead><TableHead className="text-right">Vendite</TableHead><TableHead className="text-right">Obiettivo (progr.)</TableHead><TableHead className="text-center">%</TableHead></TableRow></TableHeader>
              <TableBody>
                {agencies.map((a) => {
                  const pr = (resp?.prorata_pct || 0) / 100;
                  const tgtProg = a.target_eur ? Math.round(a.target_eur * pr) : 0;
                  const pctProg = tgtProg > 0 ? Math.round(a.fatturato_anno / tgtProg * 100) : null;
                  return (
                  <TableRow key={a.agency_id} className={pctProg != null && pctProg < 80 ? "bg-red-500/10" : ""}><TableCell className="font-semibold">{a.agency_name}</TableCell><TableCell className="text-right font-mono">{eur(a.fatturato_anno)}</TableCell><TableCell className="text-right font-mono">{tgtProg ? eur(tgtProg) : "—"}</TableCell><TableCell className="text-center font-mono">{pctProg != null ? `${pctProg}%` : "—"}</TableCell></TableRow>
                  );
                })}
              </TableBody>
              </Table>
            </>)}
          </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const KPI = ({ label, value, sub, legend, testid, onClick }) => (
  <Card className={`p-5 ${onClick ? "cursor-pointer transition-colors hover:border-primary/60" : ""}`} data-testid={testid} onClick={onClick}>
    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="mt-1 font-heading text-3xl font-black">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    {legend && <div className="mt-2 border-t border-border pt-2 text-[10px] leading-tight text-muted-foreground">{legend}</div>}
    {onClick && <div className="mt-1.5 text-[10px] font-semibold text-primary">Tocca per l'andamento mensile →</div>}
  </Card>
);

function RespAgencyDialog({ open, onClose, title, desc, rows, extraLabel }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="resp-agency-dialog">
        <DialogHeader>
          <DialogTitle>{title}{rows ? ` · ${rows.length}` : ""}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto pr-1">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Agenzia</TableHead>
              <TableHead className="text-center">{extraLabel}</TableHead>
              <TableHead className="text-right">%</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(rows || []).map((r, i) => (
                <TableRow key={i} className={r.inattiva ? "bg-amber-500/10" : ""} data-testid={`resp-ag-row-${i}`}>
                  <TableCell className="font-semibold">{r.agency_name}{r.inattiva && <span className="ml-2 rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold text-amber-500">inattiva · esclusa</span>}</TableCell>
                  <TableCell className="text-center font-mono text-xs text-muted-foreground">{r.nota}</TableCell>
                  <TableCell className="text-right font-mono">{r.pct}%</TableCell>
                </TableRow>
              ))}
              {(!rows || rows.length === 0) && <TableRow><TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">Nessun dato disponibile.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ComplianceAgencyDialog({ ag, onClose }) {
  const tot = ag ? (ag.compliance_effettuate + ag.compliance_non_effettuate) : 0;
  return (
    <Dialog open={!!ag} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="compliance-agency-dialog">
        <DialogHeader>
          <DialogTitle>% Compliance · {ag?.agency_name}</DialogTitle>
          <DialogDescription>Puntualità dell'agenzia sulle azioni di presidio (visite/contatti) dovute nel periodo: azioni svolte sul totale di quelle già attese/scadute a oggi.</DialogDescription>
        </DialogHeader>
        {ag && (
          <div className="space-y-3">
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-4xl font-black">{ag.compliance != null ? `${ag.compliance}%` : "N/D"}</span>
              <span className="text-xs text-muted-foreground">compliance</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-md border border-border p-3"><div className="font-heading text-2xl font-black text-emerald-400">{ag.compliance_effettuate}</div><div className="text-[11px] text-muted-foreground">svolte puntuali</div></div>
              <div className="rounded-md border border-border p-3"><div className="font-heading text-2xl font-black text-red-400">{ag.compliance_non_effettuate}</div><div className="text-[11px] text-muted-foreground">scadute non effettuate</div></div>
              <div className="rounded-md border border-border p-3"><div className="font-heading text-2xl font-black">{tot}</div><div className="text-[11px] text-muted-foreground">totale dovute</div></div>
            </div>
            <div className="rounded-md border border-border bg-muted/30 p-3 text-[11px] leading-relaxed text-muted-foreground">
              <b className="text-foreground">Formula</b>: compliance = azioni svolte puntuali ÷ (svolte + scadute non effettuate). Misura la puntualità dell'agenzia nel rispettare le cadenze di presidio A/B/C nel periodo selezionato.
              {ag.compliance_adjustment != null && <div className="mt-1">Impatto sulla copertura presidio: <b className="text-foreground">{ag.compliance_adjustment > 0 ? "+" : ""}{ag.compliance_adjustment}%</b></div>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NonPresidiatiDialog({ open, onClose }) {
  const [rows, setRows] = useState(null);
  const [agSel, setAgSel] = useState("all");
  const [actPartner, setActPartner] = useState(null);
  const [visitPartner, setVisitPartner] = useState(null);
  const loadRows = () => api.get(`/direzionale/non-presidiati`).then((r) => setRows(r.data.clienti || [])).catch(() => setRows([]));
  useEffect(() => {
    if (!open) return;
    setRows(null);
    setAgSel("all");
    loadRows();
  }, [open]);
  const agMap = new Map();
  (rows || []).forEach((c) => { if (!c.direzionale && c.agency_name) agMap.set(c.agency_name, (agMap.get(c.agency_name) || 0) + 1); });
  const agList = [...agMap.entries()].sort((a, b) => b[1] - a[1]);
  const shown = agSel === "all" ? (rows || []) : (rows || []).filter((c) => c.agency_name === agSel);
  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="non-presidiati-dialog">
        <DialogHeader>
          <DialogTitle>Clienti non presidiati · {shown.length}{agSel !== "all" ? ` / ${(rows || []).length}` : ""}</DialogTitle>
          <DialogDescription>Clienti della rete che richiedono presidio dall'inizio del monitoraggio (1° gen / data impostata) ma non sono in linea (fuori cadenza / in attenzione), ordinati per fatturato totale (tutto lo storico) decrescente.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2" data-testid="non-pres-filtri">
          <button type="button" onClick={() => setAgSel("all")} data-testid="non-pres-flt-all"
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${agSel === "all" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:border-primary/50"}`}>Tutti ({(rows || []).length})</button>
          <Select value={agSel} onValueChange={setAgSel}>
            <SelectTrigger className="h-8 w-[260px] text-xs" data-testid="non-pres-agenzia-filter"><SelectValue placeholder="Filtra per agenzia (presidio scaduto)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le agenzie</SelectItem>
              {agList.map(([n, ct]) => <SelectItem key={n} value={n}>{n} ({ct})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="max-h-[62vh] overflow-y-auto pr-1">
          {rows === null ? <div className="py-10 text-center text-sm text-muted-foreground">Caricamento…</div> :
           shown.length === 0 ? <div className="py-10 text-center text-sm text-muted-foreground">Nessun cliente non presidiato per questo filtro.</div> :
          <Table>
            <TableHeader><TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Agenzia</TableHead>
              <TableHead className="text-center">Cat.</TableHead>
              <TableHead className="text-center">Stato</TableHead>
              <TableHead className="text-right">Fatturato totale</TableHead>
              <TableHead>Ultima azione</TableHead>
              <TableHead className="text-center">Azioni</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {shown.map((c) => (
                <TableRow key={c.id} data-testid={`non-pres-${c.id}`}>
                  <TableCell className="font-semibold">{c.name}<span className="block text-[10px] font-normal text-muted-foreground">{[c.comune, c.provincia].filter(Boolean).join(", ")}</span></TableCell>
                  <TableCell className="text-xs">{c.direzionale ? <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-sky-400">direzionale</span> : c.agency_name}</TableCell>
                  <TableCell className="text-center"><Badge variant="secondary">{c.categoria}</Badge></TableCell>
                  <TableCell className="text-center"><Badge className={`border ${c.stato === "Fuori presidio" ? "bg-red-500/15 text-red-400 border-red-500/40" : "bg-amber-500/15 text-amber-400 border-amber-500/40"}`}>{c.stato}</Badge></TableCell>
                  <TableCell className="text-right font-mono">{eur(c.fatturato_totale)}</TableCell>
                  <TableCell className="max-w-[220px]">
                    {c.last_action_date ? (
                      <div className="text-xs" data-testid={`np-last-action-${c.id}`}>
                        <span className="font-mono text-muted-foreground">{c.last_action_date}</span>
                        {c.last_action_note && <span className="mt-0.5 block truncate text-muted-foreground" title={c.last_action_note}>{c.last_action_note}</span>}
                      </div>
                    ) : <span className="text-xs italic text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Azione rapida (telefonata / contatto)" data-testid={`np-azione-${c.id}`} onClick={() => setActPartner({ id: c.id, name: c.name })}><Zap className="h-4 w-4 text-primary" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Registra visita" data-testid={`np-visita-${c.id}`} onClick={() => setVisitPartner({ id: c.id, name: c.name })}><ClipboardCheck className="h-4 w-4 text-primary" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>}
        </div>
      </DialogContent>
    </Dialog>
    <QuickAdd hideTriggers show={["activity"]} partnerId={actPartner?.id || null} partnerName={actPartner?.name || ""} activityOpen={!!actPartner} onActivityOpenChange={(o) => { if (!o) setActPartner(null); }} onChanged={loadRows} />
    <VisitaB2BDialog open={!!visitPartner} onOpenChange={(o) => { if (!o) setVisitPartner(null); }} partners={visitPartner ? [visitPartner] : []} prospects={[]} managers={[]} actor="responsabile_commerciale" defaultPartnerId={visitPartner?.id} onSaved={loadRows} />
    </>
  );
}

function ClientiTrendDialog({ open, onClose, series, anno, totale }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="clienti-trend-dialog">
        <DialogHeader>
          <DialogTitle>Prospect → Clienti · Andamento {anno || ""}</DialogTitle>
          <DialogDescription>Numero di prospect diventati clienti per mese ({anno || "anno corrente"}). Totale progressivo: <b>{totale ?? 0}</b>.</DialogDescription>
        </DialogHeader>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={series || []} margin={{ top: 10, right: 16, bottom: 4, left: -6 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
            <Tooltip cursor={{ fill: "hsl(var(--muted))", opacity: 0.3 }}
              contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
              formatter={(v) => [v, "Clienti"]} />
            <Bar dataKey="n" fill="#F59E0B" radius={[4, 4, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </DialogContent>
    </Dialog>
  );
}

function AgencyComplianceCell({ agencyId }) {
  const [data, setData] = useState(undefined);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    setData(undefined);
    api.get(`/dashboard/my-valutazione?agency_id=${agencyId}`).then((r) => { if (alive) setData(r.data); }).catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [agencyId]);
  if (data === undefined) return <Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" />;
  const comp = data?.compliance_pct;
  if (!data || comp == null) return <span className="font-mono text-sm text-muted-foreground">N/D</span>;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`dir-agency-compliance-${agencyId}`} className="rounded-md border border-border px-2 py-1 font-mono text-sm transition-colors hover:border-primary/60 hover:bg-muted/40">{comp}%</button>
      <ComplianceDetailDialog open={open} onOpenChange={setOpen} data={data} />
    </>
  );
}

export default function Direzionale() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [af, setAf] = useState({ agent: "all", agency: "all", partner_id: "all", categoria: "all", esito: "all" });
  const [lists, setLists] = useState({ agents: [], agencies: [], partners: [] });
  const [detail, setDetail] = useState(null);
  const [trendOpen, setTrendOpen] = useState(false);
  const [nonPresOpen, setNonPresOpen] = useState(false);
  const [compAgUnused, setCompAgUnused] = useState(null);
  const [prosOpen, setProsOpen] = useState(false);
  const [pianOpen, setPianOpen] = useState(false);
  const [classSel, setClassSel] = useState(null);
  const [pdfKey, setPdfKey] = useState(null);
  const navigate = useNavigate();

  const downloadPdf = async (key, path, filename) => {
    setPdfKey(key);
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        // popup bloccato: fallback al download
        const a = document.createElement("a"); a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        toast.info("Popup bloccato: PDF scaricato.");
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Apertura PDF non riuscita"); }
    finally { setPdfKey(null); }
  };

  const PDF_DOCS = [
    { key: "contact_model", titolo: "Modello di contatto cliente", legenda: "Cadenze di visite e contatti per Categoria (A/B/C) e Stato commerciale.", path: "/reports/contact-model/pdf", filename: "modello-contatto-cliente.pdf" },
    { key: "obiettivi", titolo: "Obiettivi di vendita ed azioni per agenzia", legenda: "Obiettivo annuo, obiettivo ad oggi, vendite reali e scostamento per agenzia.", path: "/reports/obiettivi-agenzie/pdf", filename: "obiettivi-agenzie.pdf" },
    { key: "mail", titolo: "Riepilogo mail automatiche", legenda: "Elenco delle email automatiche: contenuto, destinatari e frequenza di invio.", path: "/email-settings/pdf", filename: "mail-automatiche-torri-solar.pdf" },
  ];

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/direzionale/dashboard`);
    setData(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    Promise.all([
      api.get("/users?role=commercial_manager").catch(() => ({ data: [] })),
      api.get("/agencies").catch(() => ({ data: [] })),
      api.get("/partners").catch(() => ({ data: [] })),
    ]).then(([u, ag, p]) => setLists({ agents: u.data, agencies: ag.data, partners: p.data }));
  }, []);

  const setF = (k) => (v) => setAf((s) => ({ ...s, [k]: v }));
  const resetF = () => setAf({ agent: "all", agency: "all", partner_id: "all", categoria: "all", esito: "all" });

  if (loading || !data)
    return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const pr = data.presidio;
  const maxFunnel = Math.max(1, ...data.funnel.map((s) => s.count));
  const openClass = (c) => {
    const items = (lists.partners || []).filter((p) => (p.profilazione || {}).categoria_auto === c)
      .sort((a, b) => ((b.profilazione || {}).potential_score || 0) - ((a.profilazione || {}).potential_score || 0));
    setClassSel({ c, items });
  };

  return (
    <div className="space-y-6" data-testid="direzionale-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Valutazione Rete Vendita</h1>
          <p className="text-sm text-muted-foreground" data-testid="dir-cumulative-note">Dati cumulati dall'inizio del monitoraggio ({data.monitoring_start_effective ? new Date(data.monitoring_start_effective).toLocaleDateString("it-IT") : "1° gen"}) a oggi — nessuna finestra Mese/Trimestre.</p>
        </div>
      </div>

      <Card className="p-5" data-testid="dir-pdf-docs">
        <div className="mb-3 flex items-center gap-2"><FileDown className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Impostazioni del processo</h2></div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {PDF_DOCS.map((d) => (
            <div key={d.key} className="flex flex-col justify-between rounded-lg border border-white/10 bg-black/30 p-4" data-testid={`pdf-doc-${d.key}`}>
              <div>
                <div className="font-heading text-sm font-bold">{d.titolo}</div>
                <p className="mt-1 text-[11px] leading-tight text-muted-foreground">{d.legenda}</p>
              </div>
              <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => downloadPdf(d.key, d.path, d.filename)} disabled={pdfKey === d.key} data-testid={`pdf-download-${d.key}`}>
                {pdfKey === d.key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />} Apri PDF
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <div className="relative space-y-6 rounded-xl border-2 border-primary/70 p-4 pt-6 sm:p-5 sm:pt-6" data-testid="valutazione-responsabile-perimetro">
      <span className="absolute -top-3 left-5 z-10 rounded-md bg-primary px-3 py-1 text-xs font-black uppercase tracking-wide text-primary-foreground shadow-md" data-testid="perimetro-label-valresp">Valutazione responsabile</span>
      <ClientiTrendDialog open={trendOpen} onClose={() => setTrendOpen(false)} series={data.prospect_progress?.clienti_mensile} anno={data.prospect_progress?.anno} totale={data.prospect_progress?.clienti_ytd} />
      <NonPresidiatiDialog open={nonPresOpen} onClose={() => setNonPresOpen(false)} />
      <RespAgencyDialog open={prosOpen} onClose={() => setProsOpen(false)} title="Prospect lavorati · per agenzia" desc="Prospect distinti raggiunti (giorni feriali, dal 1° gennaio) sull'obiettivo maturato, per agenzia. % max 100." rows={data.responsabile?.prospecting_detail} extraLabel="svolti/obiettivo" />
      <RespAgencyDialog open={pianOpen} onClose={() => setPianOpen(false)} title="Pianificate 2 sett. · per agenzia" desc="Media delle percentuali di pianificazione 'prossime 2 settimane' registrate ogni lunedì dal 1° gennaio, per agenzia." rows={data.responsabile?.pianificate_detail} extraLabel="settimane" />

      {data.responsabile && (
        <Card className="p-6" data-testid="dir-responsabile-card">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2"><UserRound className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Valutazione Responsabile Commerciale</h2></div>
            <MonitoringStartEditor value={data.monitoring_start} effective={data.monitoring_start_effective} onSaved={load} />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div data-testid="kpi-partner-linea-wrap">
                <KPI testid="kpi-partner-linea" label="Clienti in linea" value={`${pr.partner_in_linea}/${pr.partner_da_presidiare ?? pr.partner_totali}`} sub="clienti con cadenza di presidio rispettata" legend="Clienti che rispettano la cadenza di presidio (tutte le visite/contatti previsti effettuati) sul totale dei clienti che richiedono presidio, calcolato in modo cumulativo dall'inizio del monitoraggio a oggi con cadenze pro-rata (es. classe A: 1 visita/mese × mesi trascorsi). Sempre esclusi i clienti dormienti (classe D) e i non profilati. Tocca per l'elenco dei clienti NON presidiati ordinati per fatturato totale." onClick={() => setNonPresOpen(true)} />
              </div>
                <RespMetric testid="resp-affiancamenti" onClick={() => setDetail("affiancamenti")} label="Affiancamenti" value={data.responsabile.affiancamenti_pct != null ? `${data.responsabile.affiancamenti_pct}%` : "—"} pct={data.responsabile.affiancamenti_pct} sotto={data.responsabile.affiancamenti_sotto} sub={`${data.responsabile.affiancamenti_svolti}/${data.responsabile.affiancamenti_previsti} · obiettivo ${data.responsabile.affiancamenti_settimana}/settimana`} legend="Uscite sul campo del Responsabile a fianco delle agenzie (attività 'affiancamento agenzia' svolte) rispetto a quelle previste nel periodo." />
                <RespMetric testid="resp-conversioni" onClick={() => setDetail("conversioni")} label="Partner convertiti (rete)" value={data.responsabile.conversioni_pct != null ? `${data.responsabile.conversioni_pct}%` : "—"} pct={data.responsabile.conversioni_pct} sotto={data.responsabile.conversioni_sotto} sub={`${data.responsabile.conversioni_svolte}/${data.responsabile.conversioni_previste} · obiettivo ${data.responsabile.conversioni_agenzia_anno}/anno × agenzia`} legend="Nuovi Partner creati in tutta la rete nel periodo — sia partite da prospect sia da cliente esistente — conteggiati sulla data del primo contratto di partnership, rispetto all'obiettivo (n° nuovi partner al mese per ciascuna agenzia)." />
                <RespMetric testid="resp-copertura-fatturato" onClick={() => setDetail("copertura_fatturato")} label="Vendite rete" value={data.responsabile.copertura_fatturato != null ? `${data.responsabile.copertura_fatturato}%` : "N/D"} pct={data.responsabile.copertura_fatturato} sotto={data.responsabile.copertura_fatturato_sotto} sub={`${eur(data.responsabile.fatturato_rete)} / ${eur(data.responsabile.target_rete)} · progressivo${data.responsabile.prorata_pct != null ? ` ${data.responsabile.prorata_pct}% anno` : ""} · obiettivo ≥ ${data.responsabile.copertura_fatturato_target}%`} legend="Vendite realizzate dalla rete sul target progressivo, cioè l'obiettivo annuo rapportato ai giorni trascorsi dell'anno." />
                <RespMetric inactive={data.responsabile?.prospecting_inactive} testid="resp-prospecting" onClick={() => setProsOpen(true)} label="Prospect lavorati" value={data.responsabile.prospecting_pct != null ? `${data.responsabile.prospecting_pct}%` : "—"} pct={data.responsabile.prospecting_pct} sotto={data.responsabile.prospecting_sotto} sub={`${data.responsabile.prospecting_svolti}/${data.responsabile.prospecting_previsti} · giorni feriali, dal ${data.monitoring_start_effective ? new Date(data.monitoring_start_effective).toLocaleDateString("it-IT") : "1° gen"}`} legend="Allineato alla Compliance (Pilastro 2): prospect distinti effettivamente RAGGIUNTI (rilevati dall'AI), solo nei giorni feriali, cumulati dal 1° gennaio (o dall'attivazione dell'agenzia). Obiettivo 'maturato' = (obiettivo settimanale ÷ 5) × giorni feriali trascorsi. % di rete = somma svolti ÷ somma obiettivi di tutte le agenzie (max 100%). Tocca per il dettaglio per agenzia." />
              <RespMetric inactive={data.responsabile?.pianificate_inactive} testid="resp-pianificate-2w" onClick={() => setPianOpen(true)} label="Pianificate prossime 2 sett." value={data.responsabile.pianificate_2w_pct != null ? `${data.responsabile.pianificate_2w_pct}%` : "—"} pct={data.responsabile.pianificate_2w_pct} sotto={data.responsabile.pianificate_2w_sotto} sub={`media snapshot settimanali · dal ${data.monitoring_start_effective ? new Date(data.monitoring_start_effective).toLocaleDateString("it-IT") : "1° gen"}`} legend="Allineato alla Compliance (Pilastro 4): media delle percentuali di pianificazione 'prossime 2 settimane' registrate ogni lunedì dal 1° gennaio, aggregata su tutta la rete. Tocca per il dettaglio per agenzia." />
          </div>
          <RespDetailDialog detail={detail} onClose={() => setDetail(null)} resp={data.responsabile} agencies={data.agencies} />
        </Card>
      )}
      <PotenzialiAgenzie />
      </div>

      <div className="relative space-y-6 rounded-xl border-2 border-primary/70 p-4 pt-6 sm:p-5 sm:pt-6" data-testid="portafoglio-perimetro">
      <span className="absolute -top-3 left-5 z-10 rounded-md bg-primary px-3 py-1 text-xs font-black uppercase tracking-wide text-primary-foreground shadow-md" data-testid="perimetro-label-valagenzie">Valutazione agenzie</span>
      <Card className="p-6" data-testid="dir-agencies-card">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /><h2 className="font-heading text-lg font-bold tracking-tight">Valutazione Agenzie</h2></div>
          {data.agenzie_sotto_obiettivo > 0 && (
          <span className="rounded-full bg-red-500/15 px-3 py-1 text-xs font-bold text-red-400" data-testid="dir-alert-sotto-obiettivo">
              ⚠ {data.agenzie_sotto_obiettivo} agenzie sotto obiettivo progressivo {data.obiettivi_anno}
            </span>
          )}
        </div>
        <p className="mb-4 text-xs text-muted-foreground" data-testid="dir-agencies-purpose">Confronto di tutte le agenzie della rete: misura per ciascuna il presidio dei clienti, la copertura delle azioni, l'avanzamento sull'obiettivo di vendita e i nuovi partner generati. Serve a individuare rapidamente le agenzie sotto obiettivo (righe in rosso) su cui concentrare l'azione commerciale.</p>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agenzia</TableHead>
                <TableHead className="text-center">Clienti presidiati correttamente</TableHead>
                <TableHead className="text-center">Copertura (Grado di presidio effettivo, visite+contatti svolti sul previsto)</TableHead>
                <TableHead className="text-right">Obiettivo di vendita annuo</TableHead>
                <TableHead className="text-center">% obiettivo progressivo</TableHead>
                <TableHead className="text-center">% compliance</TableHead>
                <TableHead className="text-center">partner generati (anno)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.agencies.map((a) => (
                <TableRow key={a.agency_id} data-testid={`dir-agency-${a.agency_id}`} className={a.sotto_obiettivo ? "bg-red-500/10" : ""}>
                  <TableCell className="font-semibold">{a.agency_name}</TableCell>
                  <TableCell className="text-center font-mono">{a.partner_in_linea}/{a.partner_da_presidiare ?? a.partner_totali}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={`border ${covBadge(a.copertura_adjusted)}`}>{a.copertura_adjusted != null ? `${a.copertura_adjusted}%` : "N/D"}</Badge>
                    {a.copertura_presidio != null && a.copertura_adjusted !== a.copertura_presidio && <div className="mt-0.5 text-[10px] text-muted-foreground">base {a.copertura_presidio}%</div>}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {a.target_eur ? eur(a.target_eur) : <span className="text-muted-foreground">—</span>}
                    {a.target_eur ? <div className="text-[10px] text-muted-foreground">vend. {eur(a.fatturato_anno)}</div> : null}
                  </TableCell>
                  <TableCell className="text-center">
                    {a.target_pct_prog == null ? <span className="text-muted-foreground">—</span> :
                      <Badge className={`border ${a.target_pct_prog >= 100 ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : a.target_pct_prog >= 80 ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "bg-red-500/15 text-red-400 border-red-500/40"}`}>{a.target_pct_prog}%</Badge>}
                    {a.sotto_obiettivo && <div className="mt-0.5 text-[10px] font-semibold text-red-600">sotto obiettivo</div>}
                  </TableCell>
                  <TableCell className="text-center">
                    <AgencyComplianceCell agencyId={a.agency_id} />
                  </TableCell>
                  <TableCell className="text-center font-mono">{a.partner_generati_anno}</TableCell>
                </TableRow>
              ))}
              {data.agencies.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Nessuna agenzia.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
      </div>
    </div>
  );
}
